/**
 * Uniform-grid spatial index: O(1)-ish neighbour queries instead of O(n) full
 * scans, turning per-tick proximity work (combat target acquisition, healing,
 * later auras + the bot) from O(n^2) into O(n). Deterministic: it only PROVIDES
 * candidates — callers still apply the exact distance test + a lower-eid tie-break,
 * so the result never depends on grid/iteration order.
 */
import type { Eid } from "./types";
import type { Transform } from "./components";

export class SpatialIndex {
  private cell = 1;
  private grid = new Map<number, Eid[]>();

  /** Pack a cell (cx,cz) into one number. Maps are far smaller than 2^16 cells
   *  per axis, so the 16-bit pack never collides (negatives wrap harmlessly —
   *  a false candidate would just be distance-filtered by the caller). */
  private key(cx: number, cz: number): number {
    return (cx & 0xffff) * 65536 + (cz & 0xffff);
  }

  /** Rebuild from the live transform store. Cheap (O(n)); call at the start of any
   *  system that queries, so it always reflects current positions + entity set. */
  rebuild(cell: number, transforms: Map<Eid, Transform>): void {
    this.cell = cell;
    this.grid.clear();
    for (const [eid, tr] of transforms) {
      const k = this.key(Math.floor(tr.pos.x / cell), Math.floor(tr.pos.y / cell));
      let arr = this.grid.get(k);
      if (!arr) {
        arr = [];
        this.grid.set(k, arr);
      }
      arr.push(eid);
    }
  }

  /** Append every eid whose cell overlaps the (x,z,r) disc's bounding box into
   *  `out` (a SUPERSET of entities within r — the caller distance-filters). Reuses
   *  `out` to avoid per-call allocation in hot loops. */
  queryInto(x: number, z: number, r: number, out: Eid[]): void {
    const cell = this.cell;
    const minx = Math.floor((x - r) / cell);
    const maxx = Math.floor((x + r) / cell);
    const minz = Math.floor((z - r) / cell);
    const maxz = Math.floor((z + r) / cell);
    for (let cx = minx; cx <= maxx; cx++) {
      for (let cz = minz; cz <= maxz; cz++) {
        const arr = this.grid.get(this.key(cx, cz));
        if (arr) for (const e of arr) out.push(e);
      }
    }
  }
}
